import { FilePdfIcon } from "@/components/ui/icons";
import {
  FileCode,
  FileJson,
  FileSpreadsheet,
  FileText,
  Image,
} from "lucide-react";

export const FILE_TYPES = {
  text: {
    extensions: [".txt"],
    icon: FileText,
    language: "text",
  },
  javascript: {
    extensions: [".js", ".jsx", ".ts", ".tsx"],
    icon: FileCode,
    language: "javascript",
  },
  json: {
    extensions: [".json"],
    icon: FileJson,
    language: "json",
  },
  markdown: {
    extensions: [".md", ".mdx"],
    icon: FileText,
    language: "markdown",
  },
  image: {
    extensions: [".png", ".jpg", ".jpeg", ".gif", ".svg"],
    icon: Image,
    language: "image",
  },
  pdf: {
    extensions: [".pdf"],
    icon: FilePdfIcon,
    language: "pdf",
  },
  csv: {
    extensions: [".csv"],
    icon: FileSpreadsheet,
    language: "csv",
  },
  docx: {
    extensions: [".docx"],
    icon: FileText,
    language: "docx",
  },
  default: {
    icon: FileText,
    extensions: ["*"],
    language: "text",
  },
};

export const FILE_SEARCH_TYPES = {
  // Code files
  ".c": "text/x-c",
  ".cpp": "text/x-c++",
  ".cs": "text/x-csharp",
  ".css": "text/css",
  ".go": "text/x-golang",
  ".html": "text/html",
  ".java": "text/x-java",
  ".js": "text/javascript",
  ".json": "application/json",
  ".md": "text/markdown",
  ".pdf": "application/pdf",
  ".php": "text/x-php",
  ".pptx":
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  ".py": "text/x-python",
  ".rb": "text/x-ruby",
  ".sh": "application/x-sh",
  ".tex": "text/x-tex",
  ".ts": "application/typescript",
  ".txt": "text/plain",
  ".doc": "application/msword",
  ".docx":
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
};

export const CODE_INTERPRETER_TYPES = {
  // Code files
  ".c": "text/x-c",
  ".cs": "text/x-csharp",
  ".cpp": "text/x-c++",
  ".csv": "text/csv",
  ".doc": "application/msword",
  ".docx":
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".html": "text/html",
  ".java": "text/x-java",
  ".js": "text/javascript",
  ".json": "application/json",
  ".md": "text/markdown",
  ".pdf": "application/pdf",
  ".php": "text/x-php",
  ".pptx":
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  ".py": ["text/x-python", "text/x-script.python"],
  ".rb": "text/x-ruby",
  ".sh": "application/x-sh",
  ".tex": "text/x-tex",
  ".ts": "application/typescript",
  ".txt": "text/plain",
  ".css": "text/css",
  ".xml": ["application/xml", "text/xml"],
  ".zip": "application/zip",
  ".tar": "application/x-tar",
  ".pkl": "application/octet-stream",

  // Image formats
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".png": "image/png",
  ".gif": "image/gif",
};

export const isFileTypeSupported = (filename, tool = "file_search") => {
  const extension = "." + filename.split(".").pop().toLowerCase();
  const typeMap =
    tool === "file_search" ? FILE_SEARCH_TYPES : CODE_INTERPRETER_TYPES;
  return !!typeMap[extension];
};

export const getFileMimeType = (filename, tool = "file_search") => {
  const extension = "." + filename.split(".").pop().toLowerCase();
  const typeMap =
    tool === "file_search" ? FILE_SEARCH_TYPES : CODE_INTERPRETER_TYPES;
  const mimeType = typeMap[extension];

  // Handle array of mime types - return first one
  return Array.isArray(mimeType) ? mimeType[0] : mimeType;
};

export const fileUtils = {
  /**
   * Format file size
   * @param {number} bytes - The size in bytes
   * @returns {string} The formatted file size
   */
  formatFileSize: (bytes) => {
    if (!bytes && bytes !== 0) return "0 B";

    const units = ["B", "KB", "MB", "GB"];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  },

  generateUUID: () => {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }
    );
  },

  getFileTypeFromMime: (mimeType) => {
    if (mimeType.startsWith("image/")) return "images";
    if (mimeType.startsWith("audio/")) return "audio";
    if (mimeType.startsWith("video/")) return "videos";
    if (
      mimeType.includes("document") ||
      mimeType.includes("pdf") ||
      mimeType.includes("text")
    )
      return "documents";
    return "other";
  },

  /**
   * Validates an ETag format
   * @param {string} etag - The ETag to validate
   * @returns {boolean} Whether the ETag is valid
   */
  isValidETag: (etag) => {
    if (typeof etag !== "string") return false;
    return /^"[a-f0-9]{64}"$/.test(etag);
  },

  /**
   * Compares two ETags for equality
   * @param {string} etag1 - First ETag
   * @param {string} etag2 - Second ETag
   * @returns {boolean} Whether the ETags match
   */
  compareETags: (etag1, etag2) => {
    if (!fileUtils.isValidETag(etag1) || !fileUtils.isValidETag(etag2)) {
      throw new Error("Invalid ETag format");
    }
    return etag1 === etag2;
  },
};

export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64String = reader.result.split(",")[1];
      resolve(base64String);
    };
    reader.onerror = (error) => reject(error);
  });
};
